import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer, API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Phone, Mail, MapPin, Send, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const ContactScreen = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("General Inquiry");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { name, email, phone, subject, message });
      setSubmitted(true);
    } catch {
      toast.error("Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header variant="dark" />
        <main className="flex-1 flex items-center justify-center py-20">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">Message Sent</h2>
            <p className="text-slate-600 mb-8">Thank you for reaching out. Our team will be in touch shortly.</p>
            <Button onClick={() => navigate("/")} className="btn-primary h-12 px-8" data-testid="back-home-btn">
              Back to Home
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header variant="dark" />

      <section className="header-nucar py-16 sm:py-20">
        <div className="container-wide text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Contact Us
          </h1>
          <p className="text-slate-300 mt-3 text-base sm:text-lg max-w-xl mx-auto">
            Questions about your offer, scheduling, or anything else? We're here to help.
          </p>
        </div>
      </section>

      <main className="py-12 sm:py-16">
        <div className="container-flow">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-4">Get In Touch</h2>
                <p className="text-sm text-slate-600">Our centralized buy-center team handles all inquiries across our network of 32 dealerships.</p>
              </div>

              <div className="surface-card p-5">
                <div className="flex items-center gap-3 mb-1">
                  <Phone className="w-5 h-5 text-slate-500" />
                  <span className="font-semibold text-slate-900">Buy Center</span>
                </div>
                <p className="text-lg font-semibold text-slate-900 ml-8">1-800-781-NUCAR</p>
                <p className="text-sm text-slate-500 ml-8 mt-0.5">Mon–Sat, 9am–7pm EST</p>
              </div>

              <div className="surface-card p-5">
                <div className="flex items-center gap-3 mb-1">
                  <Mail className="w-5 h-5 text-slate-500" />
                  <span className="font-semibold text-slate-900">Email</span>
                </div>
                <p className="text-sm text-slate-600 ml-8">Submit the form and we'll respond within one business day.</p>
              </div>

              <div className="surface-card p-5">
                <div className="flex items-center gap-3 mb-1">
                  <MapPin className="w-5 h-5 text-slate-500" />
                  <span className="font-semibold text-slate-900">Locations</span>
                </div>
                <p className="text-sm text-slate-600 ml-8">32 dealerships across New England, Delaware, and Pennsylvania.</p>
                <button onClick={() => navigate("/locations")} className="text-sm text-blue-600 hover:text-blue-700 font-medium ml-8 mt-1">
                  Find your nearest location
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="surface-card p-6 sm:p-8 space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-label mb-1.5 block">NAME *</label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="h-12 rounded-xl" data-testid="contact-name" required />
                  </div>
                  <div>
                    <label className="text-label mb-1.5 block">EMAIL *</label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-12 rounded-xl" data-testid="contact-email" required />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-label mb-1.5 block">PHONE</label>
                    <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" className="h-12 rounded-xl" data-testid="contact-phone" />
                  </div>
                  <div>
                    <label className="text-label mb-1.5 block">SUBJECT</label>
                    <select value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full h-12 rounded-xl border border-slate-200 px-3 text-sm" data-testid="contact-subject">
                      <option>General Inquiry</option>
                      <option>Offer Question</option>
                      <option>Scheduling</option>
                      <option>Vehicle History</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-label mb-1.5 block">MESSAGE *</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="How can we help?"
                    rows={5}
                    className="w-full rounded-xl border border-slate-200 px-3 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                    data-testid="contact-message"
                    required
                  />
                </div>

                <Button type="submit" disabled={loading} className="h-14 px-8 btn-accent text-base" data-testid="contact-submit">
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-slate-800/30 border-t-slate-800 rounded-full animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactScreen;
